/**
 * Whether two places on a round are really the same place.
 *
 * Asked before planning, because the answer the planner gives otherwise is a
 * round that arrives somewhere it is already standing: a second stop at the
 * same coordinates, a leg of zero minutes, and a stay nobody meant to ask
 * for. Better to say so while the form is still open than to draw it.
 */

/** A point on the round, however it was given. */
export interface Located {
  lat: number;
  lng: number;
  /** The place record behind it, where there is one. Marked spots have none. */
  id?: string | undefined;
  /** What to call it in the message. */
  label: string;
}

/**
 * How close two points have to be to count as one place, in metres.
 *
 * Fifty, which is what the planner itself uses to decide a round returns to
 * where it started. Matching that matters: a gap this side of it is a round
 * trip to the planner, so a form that called the same pair "different" would
 * be disagreeing with the thing it is about to ask.
 */
const SAME_PLACE_M = 50;

/** Metres between two points, near enough for a tolerance this coarse. */
function metresBetween(a: Located, b: Located): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat = toRad((a.lat + b.lat) / 2);

  const x = dLng * Math.cos(lat);

  return Math.sqrt(dLat * dLat + x * x) * R;
}

/**
 * Whether two points are the same place.
 *
 * By identity first: two records with the same id are one place however
 * their coordinates were rounded. Then by distance, which is what catches a
 * spot marked on top of a place already on the round — the case no id can
 * answer, and the one somebody is most likely to create by accident.
 */
export function samePlace(a: Located, b: Located): boolean {
  if (a.id && b.id) return a.id === b.id;

  return metresBetween(a, b) < SAME_PLACE_M;
}

/**
 * What is wrong with this round's places, in the order worth reading.
 *
 * Returns plain sentences, empty when there is nothing to say. The caller
 * decides whether to refuse the plan or merely warn; this only reports.
 *
 * `roundTrip` is the one thing that cannot be inferred here. A round that
 * finishes where it started has start and end at the same place ON PURPOSE,
 * and that is how the planner is told so — which makes it indistinguishable
 * from the mistake unless the form says which it meant.
 */
export function samePlaceProblems(
  { start, end, stops, roundTrip }: {
    start: Located | null;
    end: Located | null;
    stops: Located[];
    roundTrip: boolean;
  },
): string[] {
  const problems: string[] = [];

  if (!roundTrip && start && end && samePlace(start, end)) {
    problems.push(
      `The round starts and ends at ${start.label}. Switch on returning to the `
      + 'departure point, or choose somewhere else to finish.',
    );
  }

  for (const stop of stops) {
    // Named once per end, because a stop that is both is two mistakes only
    // in a round trip, where it is really one.
    if (start && samePlace(stop, start)) {
      problems.push(`${stop.label} is where the round starts, so it cannot also be a stop.`);
      continue;
    }
    if (end && samePlace(stop, end)) {
      problems.push(`${stop.label} is where the round ends, so it cannot also be a stop.`);
    }
  }

  /*
    Two stops at one place.

    Rarer than the ends, and possible only by marking a spot on top of a
    place already chosen — but the round it produces is the same nonsense,
    so it is worth the pass.
  */
  for (let i = 0; i < stops.length; i += 1) {
    for (let j = i + 1; j < stops.length; j += 1) {
      const a = stops[i];
      const b = stops[j];
      if (a && b && samePlace(a, b)) {
        problems.push(`${a.label} and ${b.label} are the same place, so the round would stop twice.`);
      }
    }
  }

  return problems;
}
